import {
  ForwardedRef,
  RefObject,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react'
import { Gender, Location } from '@weblite/sahab-profile-action/profile'
import { LIMITS } from '@weblite/permission'
// Material
import { Box, Divider } from '@mui/material'
import styled from '@emotion/styled'
// Components
import {
  EditAccountProfileBox,
  EditAccountProfileBoxApiType,
} from './edit-account-profile-box'
// Common
import { Label } from '$common/label'
import { TextInput } from '$common/input'
import { SelectButton } from '$common/buttons'
// Localization
import { t } from '$i18n'
// Helpers
import { getClientUser } from '$logic/client-user'
import { getProfile, getReadableLocation } from '$logic/profile/service'
import { showSelectCounty, showSelectGender } from '$comp/modals/select-menu'
import { showBirthdatePickerModal } from '$comp/modals/date-picker'
import { dateFormatter } from '$func/time'

const { userConstraints } = LIMITS

const StyledBox = styled(Box)`
  margin-top: 10px;
  border-radius: 25px 25px 0 0;
  height: fit-content;
  position: relative;
  padding: 80px 20px 0;
  background-color: #fff;
`

const IntendedDivider = styled(Divider)`
  border-color: #e5e5e5;
  margin-bottom: 10px;
`

export interface EditAccountContentBoxApiType {
  getLocation: () => Location
  getGender: () => Gender
  getBirthDate: () => string
  getFirstname: () => string
  getLastname: () => string
}

export const EditAccountContentBox = forwardRef(
  function EditAccountContentBoxComponent(
    { forwardedRef }: { forwardedRef: RefObject<EditAccountContentBoxApiType> },
    imageRef: ForwardedRef<EditAccountProfileBoxApiType>,
  ) {
    const user = getClientUser()
    const profile = getProfile()
    const [firstname, setFirstname] = useState<string>(user.firstname)
    const [lastname, setLastname] = useState<string | undefined>(user.lastname)
    const [location, setLocation] = useState<Location>(profile.location)
    const [gender, setGender] = useState<Gender>(profile.gender)
    const [birthDate, setBirthDate] = useState<string>(profile.birthDate)

    useImperativeHandle(
      forwardedRef,
      () => ({
        getBirthDate: () => birthDate,
        getFirstname: () => firstname,
        getGender: () => gender,
        getLastname: () => lastname || '',
        getLocation: () => location,
      }),
      [birthDate, firstname, gender, lastname, location],
    )

    return (
      <StyledBox>
        <EditAccountProfileBox ref={imageRef} />
        <IntendedDivider />
        <Label title={t('user.name')} />
        <TextInput
          placeholder={t('user.enterName')}
          value={firstname}
          onChange={setFirstname}
          maxLength={userConstraints.firstname.maxLength}
        />

        <Label title={t('user.lastName')} />
        <TextInput
          placeholder={t('user.enterLastName')}
          value={lastname || ''}
          onChange={setLastname}
          maxLength={userConstraints.lastname.maxLength}
        />

        <Label title={t('user.location')} />
        <SelectButton
          value={location ? getReadableLocation(location) : undefined}
          onClick={() => showSelectCounty(setLocation)}
        />

        <Label title={t('myAccountEdit.birthDate')} exclamationMode />
        <SelectButton
          value={
            birthDate ? dateFormatter.format(new Date(birthDate)) : undefined
          }
          onClick={() =>
            showBirthdatePickerModal(
              date =>
                // NOTE: Format date to yyyy-mm-dd
                setBirthDate(date.toISOString().split('T')[0]),
              new Date(birthDate),
            )
          }
        />

        <Label title={t('myAccountEdit.gender')} exclamationMode />
        <SelectButton
          value={
            gender
              ? gender === Gender.MALE
                ? t('common.male')
                : t('common.female')
              : null
          }
          onClick={() => showSelectGender(setGender)}
        />
      </StyledBox>
    )
  },
)
